import { useRef, useState } from "react";
import { FormikProps } from "formik";

import { EmployeePledgeFormProps, ContributionOption, PaymentOption } from "../../../../../types";
import { toOptions } from "../../../../../utility/helpers/lookupHelper";
import { useWindowDimensions } from "../../../../../utility/helpers";
import { IsDesktop } from "../../../../../utility/lib/breakpoints";
import CreditCardDetails from "../../CreditCardDetails";
import usePledgeStyles from "../../styles";
import {
    DATA_PRIVACY_TEXT,
    GIFT_INVESTMENT_OPTION_OTHER_ID,
    TAX_TEXT,
    UNDESIGNATED_OPTION_ID,
} from "../../constants";
import {
    buildGiftInvestmentAmounts,
    getContributionAmount,
    updateGiftInvestmentAmount,
} from "../pledgeFormLogic";
import { EmployeePledgeViewModel } from "../../../../../viewModels";
import { EmployeePledgeGiftInvestmentViewModel } from "../../../../../viewModels/employeePledgeGiftInvestmentViewModel";

type SetFieldValue = (field: string, value: unknown) => Promise<unknown>;

interface EnrollmentValues {
    leadershipEnrollment?: boolean | null;
    stepUpEnrollment?: boolean | null;
}

interface ContributionFormValues extends EnrollmentValues {
    giftInvestment: Array<string | EmployeePledgeGiftInvestmentViewModel> | null;
    giftInvestmentAmount: EmployeePledgeGiftInvestmentViewModel[] | null;
}

export const useEmployeePledgeForm = (props: EmployeePledgeFormProps) => {
    const { width } = useWindowDimensions();
    const [isEnableReinitialize, setIsEnableReinitialize] =
        useState<boolean>(true);
    let giftInvestmentInitialVal = props.initialVal.giftInvestment;
    const {
        stepUpEnrollmentEnabled,
        leadershipEnrollmentEnabled,
        stepUpMinDonationAmount,
        leadershipMinDonationAmount,
    } = props.pledgeQuery || {};
    const formRef = useRef<FormikProps<EmployeePledgeViewModel>>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
    const [enableOtherAnnualPledgeAmount, setOtherAnnualPledgeAmountState] =
        useState<boolean>(
            !props.initialVal.contributionOptionId ||
            props.initialVal.contributionOptionId === ContributionOption.Other
        );
    const [enableOtherAnnualGiftPledge, setOtherAnnualPledgeGiftState] =
        useState<boolean>(false);
    const [pledgeAmount, setPledgeAmount] = useState<number | null | undefined>(
        props.initialVal.otherAnnualPledgeAmount
    );
    const [showSpouseOtherOptions, setShowSpouseOtherOptions] =
        useState<boolean>(false);
    const [originalAmounts, setOriginalAmounts] = useState<
        EmployeePledgeGiftInvestmentViewModel[] | null
    >(null);
    const [minimumContributionMessage, setMinimumContributionMessage] = useState<
        string | null
    >(null);

    const { contributionOption, paymentOption, giftInvestment, agencies } =
        props.lookups ?? {};
    const contributionOptions = toOptions(contributionOption ?? []);
    const agencyOptions = toOptions(agencies ?? []);
    const sortedAgency = agencyOptions;
    const paymentOptions = toOptions(paymentOption ?? []);
    const undesignatedLabel =
        "Undesignated - Will go to the Greatest Impact fund";

    const giftInvestmentOptions = toOptions(giftInvestment ?? []);
    const pledgeStyles = usePledgeStyles();

    const filteredGiftInvestmentOptions = () => {
        if (enableOtherAnnualGiftPledge) {
            return giftInvestmentOptions.filter(
                (x) => x.value !== UNDESIGNATED_OPTION_ID
            );
        }

        return giftInvestmentOptions.filter(
            (x) => x.value !== UNDESIGNATED_OPTION_ID
        );
    };

    function setOtherGiftOption(pledgeAmount: number | null | undefined) {
        setPledgeAmount(pledgeAmount);
        setOtherAnnualPledgeGiftState((pledgeAmount ?? 0) >= 250);
    }

    const displayBottom = (
        <>
            {TAX_TEXT}
            <br />
            <br />
            {DATA_PRIVACY_TEXT}
        </>
    );

    const handleGiftInvestmentChange = (
        values: Array<string> | null,
        setFieldValue: SetFieldValue,
        contributionAmount: number | null,
        giftInvestmentAmount: EmployeePledgeGiftInvestmentViewModel[] | null
    ) => {
        const currentSelections: unknown[] =
            formRef.current?.values.giftInvestment ?? [];
        const selectedIds = values ?? currentSelections.filter(
            (gift): gift is string => typeof gift === "string"
        );
        giftInvestmentInitialVal = buildGiftInvestmentAmounts({
            selectedIds,
            existingAmounts: giftInvestmentAmount,
            giftOptions: giftInvestmentOptions,
            employeeId: props.employee?.id,
            pledgeTotal: contributionAmount ?? pledgeAmount ?? 0,
        });
        setFieldValue("giftInvestmentAmount", giftInvestmentInitialVal);
    };


    const checkMinimumContribution = (
        contributionAmount: number,
        formValues: EnrollmentValues
    ) => {
        const minimumContributionAmount = getMinimumDonationAmount(
            formValues.leadershipEnrollment ?? false
        ) ?? 0;
        if (
            contributionAmount < minimumContributionAmount &&
            (formValues?.leadershipEnrollment || formValues?.stepUpEnrollment)
        ) {
            setMinimumContributionMessage(
                `To continue to receive the ${formValues?.leadershipEnrollment ? "Leadership" : "Step Up"
                } incentives a minimum donation of $${minimumContributionAmount.toLocaleString()} is required.
          Would you still like to enroll?`
            );
        }
    };

    const handleContributionOptionChange = (
        value: string,
        setFieldValue: SetFieldValue,
        giftInvestmentAmount: EmployeePledgeGiftInvestmentViewModel[] | null,
        paymentOptionId: string | null,
        formValues: ContributionFormValues
    ) => {
        const isOtherAmount = value === ContributionOption.Other;
        const contributionAmount = getContributionAmount(value, paymentOptionId);

        setOtherAnnualPledgeAmountState(isOtherAmount);
        if (!isOtherAmount) {
            checkMinimumContribution(contributionAmount ?? 0, formValues);
        }

        setOtherGiftOption(contributionAmount);
        setFieldValue("contributionAmount", contributionAmount ?? 0);
        if ((contributionAmount ?? 0) < 250) {
            setFieldValue(
                "giftInvestment",
                (formRef.current?.values.giftInvestment ?? []).filter(
                    (gift) =>
                        typeof gift === "string" &&
                        gift !== GIFT_INVESTMENT_OPTION_OTHER_ID
                )
            );
        }
        handleGiftInvestmentChange(
            formValues.giftInvestment?.filter(
                (gift): gift is string => typeof gift === "string"
            ) ?? null,
            setFieldValue,
            contributionAmount ?? 0,
            giftInvestmentAmount
        );
    };


    const recalculateRemainder = (
        values: EmployeePledgeGiftInvestmentViewModel[],
        setFieldValue: SetFieldValue,
        updatedValue: number,
        giftInvestmentId: string | null,
        agencyId: string | null
    ) => {
        setFieldValue(
            "giftInvestmentAmount",
            updateGiftInvestmentAmount({
                allocations: values,
                updatedValue,
                giftInvestmentId,
                agencyId,
                pledgeTotal: pledgeAmount ?? 0,
                employeeId: props.employee?.id,
            })
        );
    };


    const saveInput = () => {
        setIsEnableReinitialize(false);
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const creditCardDeductionOptionIndex = paymentOptions.findIndex(
        (option) => option.value === PaymentOption.CreditCard
    );
    if (creditCardDeductionOptionIndex !== -1) {
        paymentOptions[creditCardDeductionOptionIndex].children = (
            <CreditCardDetails />
        );
    }

    const openEditDialog = (
        editAmounts: EmployeePledgeGiftInvestmentViewModel[]
    ) => {
        setOriginalAmounts(editAmounts);
        setIsEditModalOpen(true);
    };

    const closeEditDialog = async (setFieldValue: SetFieldValue) => {
        await setFieldValue("giftInvestmentAmount", originalAmounts);
        setIsEditModalOpen(false);
        await setOriginalAmounts(null);
    };

    const isDesktop = IsDesktop();

    const getMinimumDonationAmount = (isLeadership: boolean | null) => {
        if (isLeadership) {
            return leadershipMinDonationAmount;
        }

        if (stepUpEnrollmentEnabled) {
            return stepUpMinDonationAmount;
        }
    };


    return {
        width,
        isEnableReinitialize,
        setIsEnableReinitialize,
        formRef,
        isEditModalOpen,
        setIsEditModalOpen,
        enableOtherAnnualPledgeAmount,
        enableOtherAnnualGiftPledge,
        pledgeAmount,
        showSpouseOtherOptions,
        setShowSpouseOtherOptions,
        minimumContributionMessage,
        setMinimumContributionMessage,
        contributionOptions,
        sortedAgency,
        paymentOptions,
        pledgeStyles,
        undesignatedLabel,
        filteredGiftInvestmentOptions,
        setOtherGiftOption,
        displayBottom,
        handleGiftInvestmentChange,
        checkMinimumContribution,
        handleContributionOptionChange,
        recalculateRemainder,
        saveInput,
        openEditDialog,
        closeEditDialog,
        isDesktop,
        stepUpEnrollmentEnabled,
        leadershipEnrollmentEnabled,
        stepUpMinDonationAmount,
        leadershipMinDonationAmount,
    };
};
